/**
 * ClockworkCanopy -- Level 3.
 *
 * Redwood peaks with clockwork observatories, giant glass lenses, star charts.
 * Theme: Integration and Harmony -- final level requiring mastery of all
 * abilities plus both co-op powers (Resonant Drop + Chronos Bubble).
 *
 * Puzzles (in order) -- each is a self-contained `PuzzleModule` under
 * `../levels/puzzles/clockwork-canopy/`:
 *   1. Gear Alignment  -- `GearAlignmentPuzzle`
 *   2. Light Path      -- `LightPathPuzzle`
 *   3. Timed Resonance -- `TimedResonancePuzzle`
 *   4. Shadow Relay    -- `ShadowRelayPuzzle`
 *   5. The Orrery      -- `TheOrreryPuzzle` (apex — gates on the other four)
 *
 * This file is now the scene shell only: boot, camera, lighting,
 * post-processing, environment art, the Elder encounter, and level
 * completion. All puzzle geometry + logic lives in the puzzle modules.
 *
 * Elder Leo returns for the final encounter, teaching the Wisdom of
 * Integration. The Elder cutscene is triggered by `PORTAL_ENTERED` once
 * the apex puzzle activates the exit portal.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { SpotLight } from "@babylonjs/core/Lights/spotLight";
import { ParticleSystem } from "@babylonjs/core/Particles/particleSystem";
import { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import "@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import "@babylonjs/core/Rendering/depthRendererSceneComponent";
import { applyVisualProfile, CLOCKWORK_CANOPY_PROFILE } from "../levels";
import { getOrMakeNoiseBump } from "../systems/ProceduralNoise";
import { installCanopyShadowView } from "../levels/art/CanopyShadowView";
import { CanopySurfaces } from "../levels/art/CanopySurfaces";
import { CanopyScenery } from "../levels/art/CanopyScenery";
import { CanopyShadowShelter } from "../levels/art/CanopyShadowShelter";
import { canopySpawns } from "../levels/layouts/CanopySpawns";
import { CanopyAtmosphere } from "../levels/art/CanopyAtmosphere";
import { CanopyParlorPresentation } from "../levels/art/CanopyParlorPresentation";

import { PhysicsSystem } from "../systems/PhysicsSystem";
import { CameraSystem } from "../systems/CameraSystem";
import { InputManager } from "../systems/InputManager";
import { AbilityHolder } from "../systems/AbilitySystem";
import { AudioManager } from "../systems/AudioManager";
import { CutsceneSystem, CutsceneStep } from "../systems/CutsceneSystem";

import { InteractiveInfo } from "../abilities/minna/AuraSight";
import { ShadowZoneInfo } from "../abilities/minna/ShadowSlip";
import { ResonantDrop } from "../abilities/coop/ResonantDrop";
import { ChronosBubble } from "../abilities/coop/ChronosBubble";
import { ElderLeo } from "../entities/npcs/ElderLeo";
import { GreaseCache } from "../entities/interactives/GreaseCache";

import { CLOCKWORK_CANOPY, COLORS, WORLD_DEBUG, PLAYER } from "../config/GameConfig";
import { tagAs } from "../levels";
import { gameEvents } from "../core/EventBus";
import { GameEvents } from "../config/events";
import { ELDER_LEO_CANOPY_ENCOUNTER, LEVEL_INTRO, controlHintForMode } from "../config/dialogue";

import { setupBaseLevel } from "./BaseLevel";
import { tryCoopAbility } from "./coopAbility";
import { setupElderBiscuitAnchor } from "./setupElderBiscuitAnchor";

import { CLOCKWORK_CANOPY_PLATFORMS, platformDepth, platformHeight, platformTop } from "../levels/layouts";
import type { PuzzleContext, PuzzleModule, PuzzleState } from "../levels/PuzzleModule";
import { GearAlignmentPuzzle } from "../levels/puzzles/clockwork-canopy/GearAlignment";
import { LightPathPuzzle } from "../levels/puzzles/clockwork-canopy/LightPath";
import { TimedResonancePuzzle } from "../levels/puzzles/clockwork-canopy/TimedResonance";
import { ShadowRelayPuzzle } from "../levels/puzzles/clockwork-canopy/ShadowRelay";
import { TheOrreryPuzzle } from "../levels/puzzles/clockwork-canopy/TheOrrery";
import { LevelCueDriver } from "../levels/LevelCueDriver";

// ─── Types ──────────────────────────────────────────────────────────

export interface ClockworkCanopyContext {
  physicsSystem: PhysicsSystem;
  cameraSystem: CameraSystem;
  inputManager: InputManager;
  minna: import("../entities/players/Minna").Minna;
  basil: import("../entities/players/Basil").Basil;
  minnaAbilities: AbilityHolder;
  basilAbilities: AbilityHolder;
  audioManager: AudioManager;
  cutsceneSystem: CutsceneSystem;
}

// ─── Main Setup ─────────────────────────────────────────────────────

/**
 * Set up the Clockwork Canopy scene.
 * Call after the engine and scene are created.
 */
export async function setupClockworkCanopy(scene: Scene): Promise<ClockworkCanopyContext> {
  // --- Base level setup ---
  const base = await setupBaseLevel(scene, {
    levelKey: "ClockworkCanopy",
    clearColor: new Color4(0.02, 0.04, 0.1, 1.0),
    fogColor: Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.GOLD_FOG),
    fogDensity: 0.008,
    // Staggered on the real entry deck using each unchanged capsule height.
    ...canopySpawns(),
    verticalFraming: CLOCKWORK_CANOPY.CAMERA_FRAMING,
    auraSightVisualConfig: { glowIntensity: .32, threadRadius: .012, threadBrightness: .4 },
    calmingResonanceVisualConfig: {
      ringThickness: .02, ringOpacity: .4, ringFadeExponent: 2,
      ringPlane: "upright", ringHeightOffset: 0,
    },
    turnTableTouchConfig: { visualRadius: .85, visualDepthOffset: .8 },
    abilityVfxVisualConfig: { chronosSphere: false, particleDensityScale: .25, particleSizeScale: .65 },
    resonanceStreamOpacity: .18,
  });

  const {
    physics: physicsSystem, minna, basil, minnaAbilities, basilAbilities,
    auraSight, shadowSlip, calmingResonance, turnTableTouch, pneumaticAnchor,
    camera: cameraSystem, audioManager, cutsceneSystem, inputManager,
  } = base;

  // --- Visual profile: lights, shadows, post-process, glow, fog, clearColor ---
  const visualHandles = applyVisualProfile(scene, CLOCKWORK_CANOPY_PROFILE, {
    glowLayer: base.getGlowLayer(),
  });
  base.addDisposeCallback(() => visualHandles.dispose());

  const glowLayer = visualHandles.glowLayer;
  const shadowGen = visualHandles.shadowGenerator!;
  base.shadowGenerator = shadowGen;

  // --- Clockwork-specific bioluminescent SpotLight ---
  // The AccentLightPreset type only supports PointLight, so this cool
  // blue canopy accent remains inline. It gives the treetops a single
  // focused beam in contrast to the warm brass accents.
  const bioLight = new SpotLight(
    "bioCanopy",
    new Vector3(60, 15, -2),
    new Vector3(0, -1, 0.3),
    Math.PI / 3,
    2,
    scene,
  );
  bioLight.diffuse = Color3.FromHexString(COLORS.BIOLUMINESCENT_BLUE);
  bioLight.intensity = 0.5;
  bioLight.range = 20;

  // Generated treetop scenery is adopted after the physical surfaces exist.
  // Its single owner replaces the diagnosed parallax/environment/scatter stack.

  // --- C5: Orrery cathedral atmosphere ------------------------------
  // The apex is the game's finale — the bar from book p.25-28 is
  // "majestic, lucid presence." Three atmosphere adds here:
  //   (a) Sparse, soft observatory motes installed after feedback owners.
  //       The old textureless cosmic emitter never rendered.
  //   (b) A warm brass glow-ramp light pinned to the orrery platform —
  //       pulses gently between 0.6 and 1.4 intensity so the brass
  //       reflection doesn't sit flat.
  //   (c) The supported celestial reading parlor, installed at the apex.

  const orreryGlow = new PointLight(
    "orreryBrassGlow",
    // Illuminate the armillary above the controls. The old position nearly
    // touched the mounted brass dial and produced a blown-out center spot.
    new Vector3(125, 9.3, -0.7),
    scene,
  );
  orreryGlow.diffuse = Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.BRASS_WARM);
  orreryGlow.specular = new Color3(1.0, 0.8, 0.4);
  orreryGlow.intensity = 0.8;
  orreryGlow.range = 12;
  base.addDisposeCallback(() => orreryGlow.dispose());
  // Ramp the brass glow on a slow sin so the orrery looks like a warm
  // breathing mechanism rather than a static set-piece.
  base.addDisposeCallback(
    base.registerSafeBeforeRender("canopyOrreryGlow", () => {
      const t = performance.now() / 1000;
      orreryGlow.intensity = 0.8 + Math.sin(t * 0.9) * 0.35;
    }),
  );

  // --- Ground & Platforms ---
  createGroundAndPlatforms(scene, physicsSystem, shadowGen);
  const canopySurfaces = new CanopySurfaces(scene, shadowGen);
  const canopyScenery = new CanopyScenery(scene, canopySurfaces, shadowGen);
  base.addDisposeCallback(() => { canopyScenery.dispose(); canopySurfaces.dispose(); });
  await canopyScenery.ready;

  // Add players as shadow casters
  await Promise.all([minna.modelReady, basil.modelReady]);
  shadowGen.addShadowCaster(minna.mesh);
  shadowGen.addShadowCaster(basil.mesh);
  base.addDisposeCallback(installCanopyShadowView(scene, visualHandles.keyLight, cameraSystem.camera));

  // --- Shadow zones ---
  const shadowZones = createShadowZones(scene);
  shadowSlip.registerShadowZones(shadowZones);
  const shadowShelter = new CanopyShadowShelter(scene, shadowGen);
  base.addDisposeCallback(() => shadowShelter.dispose());

  // --- Co-op abilities (both available from Level 3 start) ---
  const resonantDrop = new ResonantDrop(scene, basil.mesh, minna.mesh, {
    ringPlane: "ground", ringHeightOffset: -.67, ringThicknessScale: .15,
    ringOpacity: .22, softParticles: true, particleDensityScale: .2,
  });
  const chronosBubble = new ChronosBubble(scene, basil.mesh, minna.mesh, {
    ringThicknessScale: .12, ringOpacity: .22, sphereOpacity: .025,
    softParticles: true, particleDensityScale: .35,
  });
  basilAbilities.addAbility(resonantDrop);
  basilAbilities.addAbility(chronosBubble);

  // --- Constellation accent lights (used by both the background and the apex puzzle) ---
  const constellationLights: PointLight[] = [];
  const constellationPositions = [
    new Vector3(30, 25, -5), new Vector3(60, 28, -5),
    new Vector3(90, 26, -5), new Vector3(120, 30, -5),
    new Vector3(45, 30, -5), new Vector3(75, 32, -5),
    new Vector3(105, 27, -5), new Vector3(135, 29, -5),
  ];
  for (let i = 0; i < constellationPositions.length; i++) {
    const star = new PointLight(`constellation_${i}`, constellationPositions[i], scene);
    star.diffuse = Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.CONSTELLATION);
    star.intensity = 0.3;
    star.range = 15;
    constellationLights.push(star);
  }

  // ─── Puzzle Modules ──────────────────────────────────────────────
  //
  // Section layout (world X positions):
  //   Section 1:  0-25   (Puzzle 1 - Gear Alignment)
  //   Section 2: 25-55   (Puzzle 2 - Light Path)
  //   Section 3: 55-80   (Puzzle 3 - Timed Resonance)
  //   Section 4: 80-110  (Puzzle 4 - Shadow Relay)
  //   Section 5: 110-140 (Apex - The Orrery + Elder)

  const orrery = new TheOrreryPuzzle(constellationLights);
  const puzzles: PuzzleModule[] = [
    new GearAlignmentPuzzle(),
    new LightPathPuzzle(),
    new TimedResonancePuzzle(),
    new ShadowRelayPuzzle(),
    orrery,
  ];

  // Level-scoped shared puzzle state (read by the apex puzzle to gate on
  // the other four). Each puzzle writes into this map before the game loop
  // tick by way of the wrapper below.
  const levelState = new Map<string, PuzzleState>();

  // AuraSight interactive collection — puzzles contribute their meshes
  // here via `ctx.registerInteractive`, then we pass the full list to the
  // ability once setup is complete.
  const allInteractives: InteractiveInfo[] = [];

  const puzzleCtx: PuzzleContext = {
    scene,
    physicsSystem,
    glowLayer,
    shadowGenerator: shadowGen,
    minna,
    basil,
    minnaAbilities,
    basilAbilities,
    auraSight,
    shadowSlip,
    calmingResonance,
    turnTableTouch,
    pneumaticAnchor,
    audioManager,
    cutsceneSystem,
    gameEvents,
    levelState,
    registerInteractive: (info) => {
      allInteractives.push(info);
    },
    addUnsub: (unsub) => base.addUnsub(unsub),
  };

  // Setup all puzzles. Done sequentially so async work (model loads) runs
  // in order — each puzzle returns quickly since nothing loads at runtime.
  for (const puzzle of puzzles) {
    await puzzle.setup(puzzleCtx);
    // Seed the state map with the initial state so apex gating can read it
    // immediately (puzzles that report `solved: false` initially are fine).
    levelState.set(puzzle.id, puzzle.getState());
  }

  // Expose loaded puzzle IDs for runtime parity testing against the
  // level's MechanicContract (see tests/integration/puzzle-id-parity.spec.ts).
  scene.metadata = {
    ...(scene.metadata ?? {}),
    loadedPuzzleIds: puzzles.map((p) => p.id),
  };

  // Register AuraSight interactives and the gear connection line.
  auraSight.registerInteractives(allInteractives);
  // Connection line: gearA -> gearB (first two interactives registered).
  if (allInteractives.length >= 2) {
    auraSight.registerConnections([[allInteractives[0], allInteractives[1]]]);
  }

  // ─── Elder Leo (hidden until encounter) ──────────────────────────

  const finaleFloorY = platformTop(CLOCKWORK_CANOPY_PLATFORMS.platforms.find(p => p.name === "plat_s5_3")!);
  const parlor = new CanopyParlorPresentation(scene, shadowGen, finaleFloorY,
    [minna.mesh,basil.mesh].flatMap(root=>[root,...root.getChildMeshes()].filter(mesh=>mesh.isVisible&&mesh.getTotalVertices()>0)));
  base.addDisposeCallback(() => parlor.dispose());
  const elderLeoPos = parlor.leoPosition;
  const elderLeo = new ElderLeo(scene, elderLeoPos, {
    seatY: parlor.seatY, yaw: parlor.yaw, shadowGenerator: shadowGen, readingLight: parlor.readingLight,
  });
  elderLeo.setRevealed(false);
  base.addDisposeCallback(() => elderLeo.dispose());

  // ── Making Biscuits anchor (Leo's green argyle sweater) ──
  // Per SPEC-making-biscuits.md §2: "Leo's green argyle sweater, draped
  // over a parlor chair." Placed near the Elder's spawn so Minna finds
  // it during the finale's contemplative beat. Hidden by default —
  // revealed via Aura Sight.
  const leoBiscuit = setupElderBiscuitAnchor({
    scene,
    elder: "leo",
    minna, inputManager,
    cutsceneSystem,
    position: parlor.anchorPosition,
    modelPath: "/assets/models/environment/canopy_leo_memory.glb",
    interactiveRegistry: allInteractives,
  });
  base.addDisposeCallback(() => leoBiscuit.dispose());

  // ─── Composite puzzle handle for QA tooling parity ───────────────
  const compositePuzzle = {
    id: "clockworkCanopyComposite",
    getState: () => ({
      puzzles: Object.fromEntries(puzzles.map((p) => [p.id, p.getState()])),
    }),
    getInternals: () => ({
      gearAlignment: puzzles[0],
      lightPath: puzzles[1],
      timedResonance: puzzles[2],
      shadowRelay: puzzles[3],
      theOrrery: puzzles[4],
    }),
  };
  const levelHandleMeta = scene.metadata?.level as
    | {
        setPuzzle?: (p: unknown) => void;
        registerPuzzles?: (puzzles: PuzzleModule[], levelState: Map<string, PuzzleState>) => void;
      }
    | undefined;
  levelHandleMeta?.setPuzzle?.(compositePuzzle);
  // Expose per-puzzle modules to the QA harness so
  // `scene.metadata.level.getQAHints()` can aggregate across them.
  // Same wire-up OvergrownConservatory does for the hybrid primitives.
  levelHandleMeta?.registerPuzzles?.(puzzles, levelState);

  // ─── Per-puzzle guidance (objective line + anchored cue chip) ─────
  //
  // L3 introduces no new abilities, so each puzzle's getCue() names the
  // ability its current step needs; the driver pins it to the thing to
  // act on, with an "(N/5)" objective line. See LevelCueDriver.
  const cueDriver = new LevelCueDriver(
    [
      { puzzle: puzzles[0], objective: "Align the brass gears" },
      { puzzle: puzzles[1], objective: "Light the lens path" },
      { puzzle: puzzles[2], objective: "Slip through the timed gate" },
      { puzzle: puzzles[3], objective: "Charge the shadow relay" },
      { puzzle: puzzles[4], objective: "Awaken the Orrery" },
    ],
    gameEvents,
    // The awakened Orrery opens the portal; the Elder waits beyond it.
    () => (levelComplete ? null : orrery.getPortalCue()),
  ).attach(scene);
  base.addDisposeCallback(() => cueDriver.dispose());

  // ─── Level state (shell only) ────────────────────────────────────

  let elderEncounterDone = false;
  let levelComplete = false;

  // ─── Portal entry triggers the Elder encounter ───────────────────
  base.addUnsub(gameEvents.on(GameEvents.PORTAL_ENTERED, () => {
    if (levelComplete) return;
    startElderEncounter();
  }));

  function startElderEncounter(): void {
    if (elderEncounterDone) return;
    elderEncounterDone = true;

    gameEvents.emit(GameEvents.ELDER_ENCOUNTER, {
      elder: "leo",
      level: "ClockworkCanopy",
    });

    const dialogueSteps: CutsceneStep[] = ELDER_LEO_CANOPY_ENCOUNTER.map((line): CutsceneStep => ({
      type: "dialogue", dialoguePlacement: "top",
      speaker: line.speaker,
      text: line.text,
      duration: line.duration,
    }));

    // Canon halo-merge beat (book p. 28): "For the first time in the
    // game, Basil does not have to stand outside the Tuning Bubble
    // to hold a lever — the machinery sustains itself. He steps into
    // the ethereal projection alongside Minna. The boundary between
    // hardware and software — between who moves and who watches — is
    // dissolved."
    //
    // We deliver this without a new CutsceneStep type by scripting a
    // pan across Minna → Basil, a callback that steps Basil alongside
    // Minna through the physics owner, and a glow-layer
    // intensity bump to sell the halo merge. The closing stanza then
    // plays over the merged frame.
    const minnaMesh = minna.mesh;
    const basilMesh = basil.mesh;
    let savedBasilPos: Vector3 | null = null;
    const mergeTarget = { x: elderLeoPos.x - .5, y: finaleFloorY + 3.4, z: 0 };

    const steps: CutsceneStep[] = [
      { type: "fade", fadeDirection: "out", duration: 800 },
      { type: "callback", fn: () => {
        base.teleportCharacter("minna", elderLeoPos.x - 1.6, finaleFloorY + PLAYER.CAPSULE_HEIGHT / 2, 0);
        base.teleportCharacter("basil", elderLeoPos.x + 1, finaleFloorY + PLAYER.CAPSULE_HEIGHT * 1.05 / 2, 0);
        elderLeo.setRevealed(true);
        cameraSystem.snapToTargets();
      }},
      { type: "fade", fadeDirection: "in", duration: 800 },
      { type: "pan", target: { x: elderLeoPos.x, y: finaleFloorY + 3.4 }, duration: 2000 },
      { type: "wait", duration: 500 },
      // Dialogue keeps all three cats grounded in the supported apex shot.
      ...dialogueSteps,
      // Halo-merge gesture — during the final shake, Basil physically
      // steps into Minna's ethereal projection. We cache his original
      // position so nothing downstream breaks if the cutscene is ever
      // replayed (level-complete disposes the scene shortly after).
      { type: "shake", duration: 800, intensity: 0.005 },
      { type: "callback", fn: () => {
        savedBasilPos = basilMesh.position.clone();
        // Adjacent capsules share the halo without colliding at one point.
        base.teleportCharacter("basil", minnaMesh.position.x + 1.1,
          finaleFloorY + PLAYER.CAPSULE_HEIGHT * 1.05 / 2, minnaMesh.position.z);
        // Elder light bumps to sell the merged halo.
        parlor.readingLight.intensity = 4.5;
      }},
      { type: "pan", target: mergeTarget, duration: 1200 },
      { type: "wait", duration: 1200 },
      { type: "fade", fadeDirection: "out", duration: 1500 },
      { type: "callback", fn: () => {
        // Restore Basil's position in case anything reads it before the
        // scene disposes on level complete.
        if (savedBasilPos) {
          base.teleportCharacter("basil", savedBasilPos.x, savedBasilPos.y, savedBasilPos.z);
        }
        completeLevelSequence();
      }},
    ];

    cutsceneSystem.play(steps);
  }

  function completeLevelSequence(): void {
    levelComplete = true;
    gameEvents.emit(GameEvents.LEVEL_COMPLETE, { level: "ClockworkCanopy" });
  }

  // ─── Dispose puzzles and Elder on scene teardown ─────────────────

  base.addDisposeCallback(() => {
    for (const puzzle of puzzles) {
      try { puzzle.dispose(); } catch (e) { console.warn("Puzzle dispose error:", e); }
    }
  });

  // ─── Wounds and Medicine: Grease Caches ─────────────────────────
  // L3 is the finale; 2 caches placed at quiet hand-offs give Basil a
  // breather before the apex orrery puzzle without making the level
  // feel "easy."
  const greaseCachesL3 = [
    new GreaseCache(scene, {
      key: "greaseCache_L3_a",
      position: new Vector3(35, 2.2, 0),
      label: "Grease Cache",
    }),
    new GreaseCache(scene, {
      key: "greaseCache_L3_b",
      position: new Vector3(96, 2.2, 0),
      label: "Grease Cache",
    }),
  ];
  base.addDisposeCallback(() => {
    for (const cache of greaseCachesL3) {
      try { cache.dispose(); } catch (e) { console.warn("GreaseCache dispose error:", e); }
    }
  });

  const atmosphere = new CanopyAtmosphere(scene, scene.particleSystems.filter(
    (ps): ps is ParticleSystem => ps instanceof ParticleSystem &&
      (ps.name === "shadowSlipShimmer" || ps.name.startsWith("greaseCache_greaseCache_L3_")),
  ));
  base.addDisposeCallback(() => atmosphere.dispose());

  // ─── Game Loop ───────────────────────────────────────────────────

  base.registerGameLoop(
    (dt, sceneTime) => {
      atmosphere.update(dt, cameraSystem.camera.position.z);
      // Tick each puzzle and refresh its entry in levelState so the apex
      // puzzle always sees up-to-date solved flags.
      for (const puzzle of puzzles) {
        puzzle.update(dt, sceneTime);
        levelState.set(puzzle.id, puzzle.getState());
      }

      // Wounds/Medicine: tick grease caches so respawn timers advance.
      for (const cache of greaseCachesL3) {
        cache.tick(dt);
      }

      // Elder Leo idle bob + glow orbit (only matters once revealed).
      if (elderLeo.mesh.isEnabled()) {
        elderLeo.update(sceneTime, dt);
      }

      // Making Biscuits: tick the minigame + proximity prompt for Leo's sweater.
      leoBiscuit.update(dt);
    },
    {
      isLevelComplete: () => levelComplete,
      onAbilityKey: (key, pressed, released) => {
        // Biscuits (B / A / D) take priority when a session is active —
        // Minna-only, so the Elder anchor handler doesn't conflict with
        // Basil's Grease Cache on `,`.
        if (leoBiscuit.onAbilityKey(key, pressed, released)) {
          return true;
        }
        // Wounds/Medicine: check nearby Grease Cache before puzzle keys.
        if (key === "," && pressed) {
          for (const cache of greaseCachesL3) {
            if (cache.isReady && cache.isInInteractRange(basil.mesh.position)) {
              if (cache.consume(base.harnessWounds, basil.mesh.position)) {
                return true;
              }
            }
          }
        }

        // Give each unsolved puzzle a chance to handle the key. Forced-
        // solved prior puzzles keep their internal meshes around for
        // scenery, but they must not consume apex inputs after
        // `startAtPuzzle` has advanced the level state.
        for (const puzzle of puzzles) {
          if (puzzle.getState().solved) continue;
          if (puzzle.onAbilityKey && puzzle.onAbilityKey(key, pressed, released)) {
            return true;
          }
        }

        // Co-op: F + M = Resonant Drop. Both co-op abilities are unlocked
        // from the start of L3 (see instantiation above). Without these
        // handlers the AbilityInputMapper chord synthesis never reaches
        // the ability, so the TimedResonance puzzle (which gates on
        // RESONANT_DROP_TRIGGERED / CHRONOS_BUBBLE_TRIGGERED events) can
        // never solve. Mirrors the L1 RedwoodScrapyard pattern.
        if (key === "f+m" && pressed) {
          tryCoopAbility(resonantDrop, "resonantDrop");
          return true;
        }

        // Co-op: G + N = Chronos Bubble
        if (key === "g+n" && pressed) {
          tryCoopAbility(chronosBubble, "chronosBubble");
          return true;
        }

        return false;
      },
    },
  );

  // ─── Start ───────────────────────────────────────────────────────

  setTimeout(() => audioManager.startAmbient(), 500);

  gameEvents.emit(GameEvents.LEVEL_STARTED, "ClockworkCanopy");

  // Opening cutscene
  const introLines = LEVEL_INTRO["ClockworkCanopy"] ?? [];
  const introSteps: CutsceneStep[] = [
    { type: "fade", fadeDirection: "in", duration: 1200, color: { r: 0, g: 0, b: 0 } },
    { type: "wait", duration: 400 },
    ...introLines.map((line): CutsceneStep => ({
      type: "dialogue", dialoguePlacement: "top",
      speaker: line.speaker,
      text: line.text,
      duration: line.duration,
    })),
    { type: "wait", duration: 300 },
    { type: "dialogue", dialoguePlacement: "top", speaker: "", text: controlHintForMode(inputManager.soloMode), duration: 3500 },
    { type: "dialogue", dialoguePlacement: "top", speaker: "", text: "All abilities are available. Resonant Drop: hold F + M together. Chronos Bubble: hold G + N together.", duration: 4000 },
    { type: "wait", duration: 300 },
  ];

  // `playDeferred` (not raw setTimeout) so that `__startAtPuzzle` can cancel
  // this pending intro before the timer fires — see
  // docs/qa/analysis/startatpuzzle-audit-2026-04-18.md.
  cutsceneSystem.playDeferred(introSteps, 600);

  return {
    physicsSystem,
    cameraSystem,
    inputManager,
    minna,
    basil,
    minnaAbilities,
    basilAbilities,
    audioManager,
    cutsceneSystem,
  };
}

// ─── Helper: Background ─────────────────────────────────────────────

function createBackground(scene: Scene): void {
  // Backdrop panels (simulated sky)
  const skyWidth = 180;
  const sky = MeshBuilder.CreatePlane("skyBackdrop", { width: skyWidth, height: 60 }, scene);
  sky.position = new Vector3(skyWidth / 2 - 20, 25, -10);
  const skyMat = new StandardMaterial("skyMat", scene);
  skyMat.diffuseColor = Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.STARLIT_SKY);
  skyMat.emissiveColor = Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.MIDNIGHT_BLUE).scale(0.3);
  skyMat.specularColor = Color3.Black();
  skyMat.backFaceCulling = false;
  sky.material = skyMat;
  sky.isPickable = false;
  tagAs(sky, "placeholder");

  // ── Parallax backdrop layers ────────────────────────────────────────
  // Three depth-staggered planes that scroll at fractional rates with
  // the active camera's X position. Addresses a Gemini fluidity review
  // note: L3 background reads as "a flat blue void with no parallax."
  //
  // Layer 0 (far): distant redwood peak silhouettes, z=-9
  // Layer 1 (mid): misty canopy fronds, z=-7
  // Layer 2 (near): foreground canopy ferns, z=-3 (scrolls fastest)
  //
  // Each layer is built from a few tall tapered boxes positioned in
  // a seeded pattern, then registered on a scene before-render hook
  // that offsets their X by a fraction of the camera delta. The
  // illusion of depth comes from different scroll speeds, not shader
  // tricks — it's a classic 2D side-scroller parallax applied to the
  // 2.5D Babylon scene.
  const parallaxLayers: Array<{
    root: TransformNode;
    rate: number;
    baseX: number;
  }> = [];

  const layerSpecs = [
    { name: "canopyFar",  z: -9,  count: 9,  rate: 0.85, tint: "#0c1226", width: 4.0, height: 22, yBase: 22 },
    { name: "canopyMid",  z: -7,  count: 7,  rate: 0.70, tint: "#111d38", width: 5.0, height: 16, yBase: 18 },
    { name: "canopyNear", z: -3,  count: 6,  rate: 0.40, tint: "#0a101c", width: 3.2, height: 10, yBase: 12 },
  ];

  let parallaxSeed = 0x5a5a5a5a | 0;
  const rp = (): number => {
    parallaxSeed = (parallaxSeed * 1664525 + 1013904223) | 0;
    return ((parallaxSeed >>> 0) / 0xffffffff);
  };

  for (const spec of layerSpecs) {
    const root = new TransformNode(`parallax_${spec.name}_root`, scene);
    const mat = new StandardMaterial(`parallax_${spec.name}_mat`, scene);
    mat.diffuseColor = Color3.FromHexString(spec.tint);
    mat.emissiveColor = Color3.FromHexString(spec.tint).scale(0.3);
    mat.specularColor = Color3.Black();
    mat.backFaceCulling = false;
    for (let i = 0; i < spec.count; i++) {
      const x = (i / spec.count) * skyWidth + (rp() - 0.5) * 6;
      const hScale = 0.7 + rp() * 0.8;
      const box = MeshBuilder.CreateBox(
        `${spec.name}_${i}`,
        {
          width: spec.width + rp() * 1.5,
          height: spec.height * hScale,
          depth: 0.6,
        },
        scene,
      );
      box.position = new Vector3(x, spec.yBase * hScale * 0.5, 0);
      box.material = mat;
      box.parent = root;
      box.isPickable = false;
      tagAs(box, "placeholder");
    }
    root.position = new Vector3(0, 0, spec.z);
    parallaxLayers.push({ root, rate: spec.rate, baseX: 0 });
  }

  // Scroll hook. Each layer moves by `(1 - rate) * camera.x` relative
  // to its baseX, so far layers (rate high) barely move and near
  // layers (rate low) track the camera more closely. The net effect:
  // moving right causes far peaks to drift left slowly while near
  // ferns drift left faster.
  scene.onBeforeRenderObservable.add(() => {
    const cam = scene.activeCamera;
    if (!cam) return;
    for (const layer of parallaxLayers) {
      layer.root.position.x = cam.position.x * (1 - layer.rate);
    }
  });

  // ── Procedural starfield ────────────────────────────────────────────
  // Hundreds of tiny emissive disc-stars scattered across the sky plane.
  // Deterministic via a seeded LCG so two runs of the harness produce
  // an identical snapshot hash. Tagged placeholder so the asset audit
  // knows these are stubs until a proper star texture lands.
  const starMat = new StandardMaterial("starMat", scene);
  starMat.diffuseColor = Color3.Black();
  starMat.specularColor = Color3.Black();
  // Full-brightness emissive so DefaultRenderingPipeline's bloom actually
  // blooms them. Stars that read dim in the shipped screenshot are being
  // clamped back down by tonemapping; giving them a 5+ multiplier puts
  // them above the bloom threshold and they pop.
  starMat.emissiveColor = new Color3(6, 6, 7);
  starMat.disableLighting = true;
  const starMatWarm = new StandardMaterial("starMatWarm", scene);
  starMatWarm.diffuseColor = Color3.Black();
  starMatWarm.specularColor = Color3.Black();
  starMatWarm.emissiveColor = new Color3(7, 5, 3);
  starMatWarm.disableLighting = true;
  let seed = 0x9e3779b9 | 0;
  const rand = () => {
    seed = (seed * 1664525 + 1013904223) | 0;
    return ((seed >>> 0) / 0xffffffff);
  };
  const starCount = 150;
  for (let i = 0; i < starCount; i++) {
    const x = -10 + rand() * (skyWidth + 10);
    // Visible y at the spawn camera frustum is roughly [-2..10] (camera
    // y=4 ± ~6 at z=-5 through a 60° FOV). Cluster stars from y=4..28
    // so some are visible at every camera altitude as cats ascend.
    const y = 4 + rand() * 24;
    const size = 0.55 + Math.pow(rand(), 2) * 0.9;
    const star = MeshBuilder.CreatePlane(`star_${i}`, { width: size, height: size }, scene);
    star.position = new Vector3(x, y, -5);
    star.material = i % 7 === 0 ? starMatWarm : starMat;
    star.isPickable = false;
    tagAs(star, "placeholder");
  }

  // ── Constellation lines ─────────────────────────────────────────────
  // Thin emissive line segments between adjacent constellation point-
  // light positions, so the orrery's "star charts" actually read as
  // charted constellations rather than scattered dots.
  const constellationNodes: Vector3[] = [
    new Vector3(5, 9, -5.0), new Vector3(18, 12, -5.0),
    new Vector3(32, 8, -5.0), new Vector3(48, 14, -5.0),
    new Vector3(66, 10, -5.0), new Vector3(82, 13, -5.0),
    new Vector3(100, 9, -5.0), new Vector3(118, 12, -5.0),
    new Vector3(136, 10, -5.0),
  ];
  const lineMat = new StandardMaterial("constellationLineMat", scene);
  lineMat.diffuseColor = Color3.Black();
  lineMat.specularColor = Color3.Black();
  lineMat.emissiveColor = new Color3(2.0, 2.2, 3.0);
  lineMat.disableLighting = true;
  for (let i = 0; i < constellationNodes.length - 1; i++) {
    const a = constellationNodes[i];
    const b = constellationNodes[i + 1];
    const mid = a.add(b).scale(0.5);
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx);
    const seg = MeshBuilder.CreateBox(`constellationLine_${i}`, { width: len, height: 0.12, depth: 0.04 }, scene);
    seg.position = mid;
    seg.rotation.z = angle;
    seg.material = lineMat;
    seg.isPickable = false;
    tagAs(seg, "placeholder");
  }

  // Redwood tree trunks (tall dark silhouettes)
  const trunkPositions = [
    new Vector3(-5, 15, -3), new Vector3(25, 18, -4),
    new Vector3(55, 16, -3), new Vector3(85, 17, -4),
    new Vector3(115, 19, -3), new Vector3(140, 16, -4),
  ];
  const trunkMat = new PBRMaterial("trunkMat", scene);
  trunkMat.albedoColor = new Color3(0.15, 0.08, 0.05);
  trunkMat.metallic = 0.0;
  trunkMat.roughness = 0.95;

  for (let i = 0; i < trunkPositions.length; i++) {
    const trunk = MeshBuilder.CreateBox(`trunk_${i}`, { width: 3, height: 40, depth: 2 }, scene);
    trunk.position = trunkPositions[i];
    trunk.material = trunkMat;
    trunk.isPickable = false;
    tagAs(trunk, "placeholder");
  }
}

// ─── Helper: Starfield (constellation patterns) ─────────────────────

function createStarfield(scene: Scene): void {
  // Small emissive spheres scattered across the sky backdrop
  const starMat = new StandardMaterial("starMat", scene);
  starMat.diffuseColor = Color3.Black();
  starMat.emissiveColor = Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.CONSTELLATION);
  starMat.specularColor = Color3.Black();

  const starPositions: Vector3[] = [];
  // Procedural star placement
  for (let i = 0; i < 60; i++) {
    const x = Math.random() * 160 - 10;
    const y = 20 + Math.random() * 25;
    const z = -8 - Math.random() * 3;
    starPositions.push(new Vector3(x, y, z));
  }

  for (let i = 0; i < starPositions.length; i++) {
    const size = 0.05 + Math.random() * 0.15;
    const star = MeshBuilder.CreateSphere(`star_${i}`, { diameter: size, segments: 4 }, scene);
    star.position = starPositions[i];
    star.material = starMat;
    star.isPickable = false;
    tagAs(star, "placeholder");
  }

  // Constellation lines (thin boxes connecting stars)
  const lineMat = new StandardMaterial("constellationLineMat", scene);
  lineMat.diffuseColor = Color3.Black();
  lineMat.emissiveColor = Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.CONSTELLATION).scale(0.3);
  lineMat.alpha = 0.4;

  // A few hand-placed constellation segments
  const constellationSegments = [
    { from: new Vector3(30, 28, -9), to: new Vector3(38, 32, -9) },
    { from: new Vector3(38, 32, -9), to: new Vector3(45, 29, -9) },
    { from: new Vector3(80, 30, -9), to: new Vector3(88, 34, -9) },
    { from: new Vector3(88, 34, -9), to: new Vector3(95, 31, -9) },
    { from: new Vector3(95, 31, -9), to: new Vector3(90, 27, -9) },
  ];

  for (let i = 0; i < constellationSegments.length; i++) {
    const seg = constellationSegments[i];
    const mid = seg.from.add(seg.to).scale(0.5);
    const len = Vector3.Distance(seg.from, seg.to);
    const line = MeshBuilder.CreateBox(`constLine_${i}`, { width: len, height: 0.03, depth: 0.03 }, scene);
    line.position = mid;
    // Rotate to face from -> to
    const dir = seg.to.subtract(seg.from).normalize();
    line.rotation.z = Math.atan2(dir.y, dir.x);
    line.material = lineMat;
    line.isPickable = false;
    tagAs(line, "placeholder");
  }
}

// ─── Helper: Ground and Platforms ───────────────────────────────────

function createGroundAndPlatforms(
  scene: Scene,
  physics: PhysicsSystem,
  shadowGen: ShadowGenerator,
): Mesh[] {
  const platforms: Mesh[] = [];

  // Ground plane
  const ground = physics.createGround(
    CLOCKWORK_CANOPY.WORLD_WIDTH,
    CLOCKWORK_CANOPY.GROUND_DEPTH,
    CLOCKWORK_CANOPY.WORLD_WIDTH / 2,
  );
  const groundMat = new PBRMaterial("groundMat", scene);
  groundMat.albedoColor = new Color3(0.1, 0.07, 0.05);
  groundMat.metallic = 0.1;
  groundMat.roughness = 0.9;
  groundMat.emissiveColor = new Color3(0.005, 0.003, 0.008);
  if (ground) {
    ground.material = groundMat;
    ground.receiveShadows = true;
  }

  // Platform material: weathered brass.
  // Emissive bumped from near-zero so the platforms actually read
  // against the dark starlit scene. Gemini 3.1-pro flagged `plat_s1_4`
  // near gear B as "invisible / Basil floats in mid-air" under the
  // prior 0.01-channel emissive — the brass needs enough internal
  // glow to survive the deep vignette + high bloom threshold of the
  // CINEMATIC_NIGHT post-process preset. Bump also gives the plank
  // surfaces variation under the pulsing brass-orrery point light.
  const platMat = new PBRMaterial("platMat", scene);
  platMat.albedoColor = Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.COPPER_DARK);
  platMat.metallic = 0.5;
  platMat.roughness = 0.6;
  platMat.emissiveColor = new Color3(0.08, 0.05, 0.02);
  const canopyPlatNoise = getOrMakeNoiseBump(scene, "canopy_platform_brass", 0.4);
  canopyPlatNoise.uScale = 6;
  canopyPlatNoise.vScale = 1;
  platMat.bumpTexture = canopyPlatNoise;
  platMat.bumpTexture.level = 0.25;
  const platformTrimMat = new PBRMaterial("canopyPlatformTrimMat", scene);
  platformTrimMat.albedoColor = Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.ORRERY_BRASS).scale(0.85);
  platformTrimMat.metallic = 0.75;
  platformTrimMat.roughness = 0.34;
  platformTrimMat.emissiveColor = new Color3(0.08, 0.055, 0.025);
  const platformRivetMat = new PBRMaterial("canopyPlatformRivetMat", scene);
  platformRivetMat.albedoColor = Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.BRASS_WARM).scale(0.75);
  platformRivetMat.metallic = 0.85;
  platformRivetMat.roughness = 0.28;
  platformRivetMat.emissiveColor = new Color3(0.12, 0.08, 0.025);

  // Multi-tier platform network — geometry lives in
  // src-babylon/levels/layouts/ so the reachability lint can check it.
  const platDefs = CLOCKWORK_CANOPY_PLATFORMS.platforms.map((def) => ({
    name: def.name,
    w: def.w,
    h: platformHeight(def),
    d: platformDepth(def),
    pos: new Vector3(def.x, def.y, 0),
  }));

  for (const def of platDefs) {
    const plat = physics.createPlatform(def.name, def.w, def.h, def.d, def.pos);
    plat.material = platMat;
    plat.receiveShadows = true;
    shadowGen.addShadowCaster(plat);
    platforms.push(plat);

    const frontTrim = MeshBuilder.CreateBox(
      `${def.name}_frontBrassTrim`,
      { width: def.w * 0.96, height: 0.08, depth: 0.08 },
      scene,
    );
    frontTrim.position.set(def.pos.x, def.pos.y + def.h / 2 + 0.09, def.pos.z + def.d / 2 + 0.05);
    frontTrim.material = platformTrimMat;
    frontTrim.receiveShadows = true;
    frontTrim.isPickable = false;
    tagAs(frontTrim, "modular-final");

    const backTrim = MeshBuilder.CreateBox(
      `${def.name}_backBrassTrim`,
      { width: def.w * 0.9, height: 0.05, depth: 0.05 },
      scene,
    );
    backTrim.position.set(def.pos.x, def.pos.y + def.h / 2 + 0.05, def.pos.z - def.d / 2 - 0.04);
    backTrim.material = platformTrimMat;
    backTrim.receiveShadows = true;
    backTrim.isPickable = false;
    tagAs(backTrim, "modular-final");

    for (const side of [-1, 1]) {
      const rivet = MeshBuilder.CreateCylinder(
        `${def.name}_trimRivet_${side > 0 ? "e" : "w"}`,
        { height: 0.08, diameter: 0.18, tessellation: 12 },
        scene,
      );
      rivet.position.set(
        def.pos.x + side * Math.max(def.w / 2 - 0.35, 0.15),
        def.pos.y + def.h / 2 + 0.16,
        def.pos.z + def.d / 2 + 0.09,
      );
      rivet.material = platformRivetMat;
      rivet.receiveShadows = true;
      rivet.isPickable = false;
      tagAs(rivet, "modular-final");
    }
  }

  // Central orrery dais. Keep it inside the gap between the sync
  // platforms so it reads as a brass focal point without becoming a
  // low collision ceiling over the approach route.
  const dais = CLOCKWORK_CANOPY_PLATFORMS.extraColliders?.find((d) => d.name === "orreryPlatform");
  if (!dais) throw new Error("ClockworkCanopy layout is missing orreryPlatform");
  const orreryPlat = physics.createPlatform(
    dais.name,
    dais.w,
    platformHeight(dais),
    platformDepth(dais),
    new Vector3(dais.x, dais.y, 0),
  );
  const orreryPlatMat = new PBRMaterial("orreryPlatMat", scene);
  orreryPlatMat.albedoColor = Color3.FromHexString(CLOCKWORK_CANOPY.COLORS.ORRERY_BRASS).scale(0.7);
  orreryPlatMat.metallic = 0.8;
  orreryPlatMat.roughness = 0.3;
  // C6: brass-hammered bump so the glancing highlights break up nicely
  // under the new orrery brass glow light (see C5).
  const orreryNoise = getOrMakeNoiseBump(scene, "canopy_orrery_brass", 0.4);
  orreryNoise.uScale = 2.2;
  orreryNoise.vScale = 1;
  orreryPlatMat.bumpTexture = orreryNoise;
  orreryPlatMat.bumpTexture.level = 0.25;
  orreryPlat.material = orreryPlatMat;
  orreryPlat.receiveShadows = true;
  shadowGen.addShadowCaster(orreryPlat);
  platforms.push(orreryPlat);

  return platforms;
}

// ─── Helper: Shadow Zones ───────────────────────────────────────────

function createShadowZones(scene: Scene): ShadowZoneInfo[] {
  // Detection retains its original names, bounds and invisibility. Supported
  // rear shade cloth is independently owned by CanopyShadowShelter.
  const definitions = [
    { position: new Vector3(10, 1.2, 0), width: 5, height: 1, depth: 1.5 },
    { position: new Vector3(92, 2, 0), width: 8, height: 3, depth: 1.5 },
    { position: new Vector3(100, 2, 0), width: 6, height: 3, depth: 1.5 },
    { position: new Vector3(125, 1.5, 0), width: 10, height: 3, depth: 1.5 },
  ];
  return definitions.map((definition, i) => {
    const mesh = MeshBuilder.CreateBox(`shadowZone_${i}`, definition, scene);
    mesh.position = definition.position;
    mesh.isVisible = false;
    mesh.isPickable = false;
    if (WORLD_DEBUG.SHOW_SHADOW_ZONE_VISUALS) {
      const visual = MeshBuilder.CreateBox(`shadowZoneVisual_${i}`, definition, scene);
      visual.position = definition.position.clone();
      visual.isPickable = false;
      const material = new StandardMaterial(`shadowZoneMat_${i}`, scene);
      material.diffuseColor = new Color3(.09, .05, .16);
      material.alpha = .18;
      visual.material = material;
    }
    return { mesh };
  });
}
